import React, { useCallback, useContext, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { Appheader } from "src/components/header/appheader/Appheader";
import { RootStoreContext } from "src/stores/RootStore";
import { useHistory, useLocation } from "react-router-dom";
import { useSystemHealthCheck } from "src/hooks/system-health-check/useSystemHealthCheck";
import { MainRoutes } from "src/screens/routes/MainRoutes";
import { Sidebar } from "src/components/sidebar/Sidebar";
import { useSidebarFunctions } from "src/hooks/sidebar-functions/useSidebarFunctions";
import styles from "./Main.module.css";
import { FeatureAnnouncementBanner } from "src/components/featureAnnouncementBanner/FeatureAnnouncementBanner";
import { TicketDemoModeOrArchivedBanner } from "src/components/ticketDemoModeOrArchivedBanner/TicketDemoModeOrArchivedBanner";
import { AssemblyLiveModeBanner } from "src/components/assemblyLiveModeBanner/AssemblyLiveModeBanner";
import { Route } from "src/config/routes";
import { FileHTMLInputEvent } from "src/stores/krediflow/InvoiceFilePreviewStore";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { InvoiceBanner } from "src/components/krediFlow/invoiceBanner/InvoiceBanner";
import { CopyUserBanner } from "./copyUser/CopyUserBanner";
import { NoticeGateModal } from "./appGate/NoticeGateModal";

const SidebarOverlayBase = () => {
    const { uiStore, uiSidebarStore } = useContext(RootStoreContext);
    const showSidebarOverlay = uiStore.isMobile && uiSidebarStore.sidebarWidth > 0;

    if (showSidebarOverlay) {
        return <div id="SidebarOverlay" className={styles.MobileSidebarOverlay}></div>;
    }

    return null;
};
const SidebarOverlay = observer(SidebarOverlayBase);

const InvoiceFilePreviewDragOverlayBase = () => {
    const { invoiceFilePreviewStore } = useContext(RootStoreContext);

    const { t } = useTranslation();

    const showDragOverlay =
        invoiceFilePreviewStore.isDraggingOverMainWindow && invoiceFilePreviewStore.isDraggingActive;

    // Disable scrolling when overlay is shown
    useEffect(() => {
        if (showDragOverlay) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [showDragOverlay]);

    if (showDragOverlay) {
        return (
            <div className={styles.DragOverlayContainer}>
                <div className={styles.DragOverlayMessage}>
                    <UI.Icon style={{ marginBottom: 10 }} size={100} color="var(--grey)" icon={UI.SVGIcon.Documents} />
                    <div className={styles.DragOverlayMessageText}>{t("screens.kredi_flow.add_document")}</div>
                </div>
            </div>
        );
    }

    return null;
};
const InvoiceFilePreviewDragOverlay = observer(InvoiceFilePreviewDragOverlayBase);

const MainRoutesBase = () => {
    const { uiStore } = useContext(RootStoreContext);
    const mainDivRef = useRef<Element>(null);

    // Resize observer effect
    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            const mainContentWindowWidthAfterResize = entries[0].contentRect.width;
            uiStore.setContentWidth(mainContentWindowWidthAfterResize);
        });

        const currentRef = mainDivRef.current;

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [uiStore]);

    return (
        <div ref={mainDivRef} id="MainWindow" className="flex-grow-1 d-flex flex-column">
            <MainRoutes />
        </div>
    );
};

export const MainBase = () => {
    const { uiStore, uiSidebarStore, navStore, invoiceStore, authStore, invoiceFilePreviewStore } =
        useContext(RootStoreContext);
    const history = useHistory();
    const { collapseSidebar } = useSidebarFunctions();

    const location = useLocation();

    const dragCounter = useRef(0);

    useEffect(() => {
        const isCurrentRouteCreateNewInvoice = location.pathname.includes(Route.newInvoice);
        const isCurrentRouteInvoice = location.pathname.includes("/creditor/");
        const isInvoiceInEditMode = invoiceStore.isEditing;

        const isAlreadyInvoiceFileSelected = invoiceStore.currentInvoice?.isFileSelected ?? false;

        const isDraggingActive =
            !isAlreadyInvoiceFileSelected &&
            (isCurrentRouteCreateNewInvoice || (isCurrentRouteInvoice && isInvoiceInEditMode));

        invoiceFilePreviewStore.setIsDraggingActive(isDraggingActive);
    }, [
        invoiceFilePreviewStore,
        invoiceStore.currentInvoice?.isFileSelected,
        invoiceStore.isEditing,
        location.pathname
    ]);

    useSystemHealthCheck();

    // History listener effect
    useEffect(() => {
        navStore.addToHistoryStack(history.location.pathname);

        return history.listen((location) => {
            navStore.addToHistoryStack(location.pathname);
        });
    }, [history, navStore]);

    const closeSidebarIfOnMobile = useCallback(
        (e: any) => {
            if (uiStore.isMobile && uiSidebarStore.sidebarWidth > 0 && e.target.id === "SidebarOverlay") {
                collapseSidebar();
            }
        },
        [collapseSidebar, uiStore.isMobile, uiSidebarStore.sidebarWidth]
    );

    const shouldRenderFeatureAnnouncementBanner =
        authStore.token && authStore.user?.customerid && authStore.user?.userid && !authStore.user?.hasOnlyEcAdminRole;

    const onDragEnter = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();

            dragCounter.current += 1;

            if (dragCounter.current === 1) {
                invoiceFilePreviewStore.setIsDraggingOverMainWindow(true);
            }
        },
        [invoiceFilePreviewStore]
    );

    const onDragLeave = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();

            dragCounter.current -= 1;
            if (dragCounter.current === 0) {
                invoiceFilePreviewStore.setIsDraggingOverMainWindow(false);
            }
        },
        [invoiceFilePreviewStore]
    );

    const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const onDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();

            dragCounter.current = 0; // Reset counter
            invoiceFilePreviewStore.setIsDraggingOverMainWindow(false);

            const files = e.dataTransfer.files;

            if (files.length > 0) {
                const syntheticFileHtmlInputEvent: FileHTMLInputEvent = {
                    target: {
                        files: files,
                        value: files.length > 0 ? files[0].name : ""
                    }
                };

                invoiceFilePreviewStore.handleFileInputChange({
                    displayOverrideExistingDataModal: invoiceStore.currentInvoice?.isNewInvoice ? false : true,
                    overrideData: true
                })(syntheticFileHtmlInputEvent);
            }
        },
        [invoiceStore.currentInvoice?.isNewInvoice, invoiceFilePreviewStore]
    );

    return (
        <main>
            <div
                className="d-flex"
                id="MainContainer"
                onClick={closeSidebarIfOnMobile}
                onDragEnter={invoiceFilePreviewStore.isDraggingActive ? onDragEnter : undefined}
                onDragOver={invoiceFilePreviewStore.isDraggingActive ? onDragOver : undefined}
                onDragLeave={invoiceFilePreviewStore.isDraggingActive ? onDragLeave : undefined}
                onDrop={invoiceFilePreviewStore.isDraggingActive ? onDrop : undefined}
            >
                <Sidebar />
                <SidebarOverlay />
                <div
                    id="ContentWindow"
                    className="d-flex flex-column flex-grow-1"
                    style={{
                        minHeight: "100vh",
                        background: uiStore.backgroundColor,
                        marginLeft: uiStore.isMobile ? "0px" : `${uiSidebarStore.sidebarWidth}px`,
                        transition: "margin-left 0.3s",
                        width: "100%",
                        minWidth: 0
                    }}
                >
                    <Appheader />
                    {shouldRenderFeatureAnnouncementBanner && <FeatureAnnouncementBanner />}
                    <TicketDemoModeOrArchivedBanner />
                    <AssemblyLiveModeBanner />
                    <CopyUserBanner />
                    <InvoiceBanner />
                    <MainRoutesBase />
                    {authStore.isLoggedIn && <NoticeGateModal />}
                    <InvoiceFilePreviewDragOverlay />
                </div>
            </div>
        </main>
    );
};

export const Main = Sentry.withProfiler(observer(MainBase));
